import { Client } from '@nrdy-marketing-engine/cart/@types/cart-state';
import { useCallback, useEffect, useState } from 'react';
import type { UserContextInterface } from './context';
import { UserController } from './user-controller';

const userController = new UserController();

function useUser(defaults?: UserContextInterface): UserContextInterface {
  const [user, setUser] = useState(defaults?.user);
  const [loading, setLoading] = useState(true);
  const [refetchUser, setRefetchUser] = useState(false);
  const [clientID, setClientID] = useState(defaults?.clientID);

  const convertUserAndStore = useCallback(() => {
    return userController
      .usersMeConvert()
      .then((data) => {
        setLoading(true);
        setRefetchUser(true);

        return data;
      })
      .catch((err) => {
        setLoading(false);

        console.error(err);
        throw err;
      });
  }, []);

  const createUserAndStore = useCallback((client: Client) => {
    return userController
      .createAccount(client, undefined)
      .then((data) => {
        setLoading(true);
        return data;
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
        throw err;
      });
  }, []);

  const fetchUserAndStore = useCallback(() => {
    userController
      .usersMeGet()
      .then((user) => updateStateUser(user))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const refetchUserAndStore = useCallback(() => fetchUserAndStore(), [fetchUserAndStore]);

  const signInAndStore = useCallback(({ email, password }: Partial<Client>) => {
    return userController
      .signIn({ email, password })
      .then((data) => {
        setLoading(true);
        setRefetchUser(true);

        return data;
      })
      .catch((err) => {
        setLoading(false);

        console.error(err);
        throw err;
      });
  }, []);

  const updateStateUser = (user) => {
    if (user?.entity) {
      setUser(user);
      setClientID(user.entity.client_uuid);
    }
  };

  useEffect(
    function _refetchUser() {
      if (refetchUser) {
        setRefetchUser(false);
        fetchUserAndStore();
      }
    },
    [refetchUser, fetchUserAndStore]
  );

  useEffect(
    function _initialUserSetup() {
      if (!user && loading) fetchUserAndStore();
    },
    [user, loading, fetchUserAndStore]
  );

  return {
    clientID,
    convertUserAndStore,
    createUserAndStore,
    loading,
    signInAndStore,
    refetchUserAndStore,
    user,
  };
}

export default useUser;
